import { ICouponServiceProps } from "@/api_framework/api_modals/coupon";
import SimpleCheckbox from "@/app/components/ui/CheckBoxes/SimpleCheckbox";
import RadioGroupComponent from "@/app/components/ui/RadioGroup";
import MultiSelectDropdown from "@/app/components/ui/SelectMenus/MultiSelectDropdown/index";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import Toggle from "@/app/components/ui/Toggles";
import { useStaffClinicListAPI } from "@/app/hooks/api_hooks/clinic/useStaffClinicListAPI";
import { useConfigurationCityListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityListAPI";
import { useCouponDetailsAPI } from "@/app/hooks/api_hooks/coupon/useCouponDetailsAPI";
import { useCouponUpdationAPI } from "@/app/hooks/api_hooks/coupon/useCouponUpdationAPI";
import { useCouponUserPerSonaListAPI } from "@/app/hooks/api_hooks/coupon/useCouponUserPerSonaListAPI";
import { usePincodeListingAPI } from "@/app/hooks/api_hooks/coupon/usePincodeListingAPI";
import { useOrgServicesByFilterAPI } from "@/app/hooks/api_hooks/org/useOrgServicesByFilterAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { ICouponServiceState } from "@/app_redux/reducers/slices/auth/coupon_service_slice";
import { IPatientWaitingListState } from "@/app_redux/reducers/slices/auth/patient_waiting_list_slice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";

export interface ICouponServiceApplicabilityConditionFromFiled {
  minimum_cart_value_required: string;
  minimum_cart_value: string | null;
  applicable_city: Array<string>;
  applicable_clinics: Array<string>;
  included_segments: string;
  included_operation: string;
  excluded_segments: boolean;
  excluded_operation: string;
  include_persona: Array<string>;
  exclude_persona: Array<string>;
  allpersona: string;
  allexclude: boolean;
  pincodes: string[];
  pincodeEligibility: boolean;
  base_coupon: boolean;
  appointment_dependency: boolean;
  appointment_sub_services: string[];
}

const CouponApplicabilityComponent = () => {
  const { getRouteKey } = useRouter();
  const [params, _setParams] = useSearchParams();
  const { routeState } = useRouteInfo(getRouteKey("PATIENT_WAITING_LIST", "id"));
  const { routeState: newRouteState } = useRouteInfo(getRouteKey("COUPON_SERVICE", "id"));
  const { routeState: configurationRouteState } = useRouteInfo(getRouteKey("CONFIGURATION", "id"));
  const [isDisable, setIsDisable] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { clinics } = routeState.state as IPatientWaitingListState;
  const { cityList } = configurationRouteState.state as IConfigurationListState;
  const { pincodeListing } = newRouteState.state as ICouponServiceState;
  const [perSonaItem, setPerSonaItem] = useState([]);
  const [excludeperSonaItem, setExcludeperSonaItem] = useState([]);
  const navigation = useNavigate();
  const [initialfetch, setInitialfetch] = useState(false);
  const [initialPincodeFetch, setInitialPincodeFetch] = useState(false);
  const [allCitiesSelected, setAllCitiesSelected] = useState(false);
  const [allClinicsSelected, setAllClinicsSelected] = useState(false);
  const coupon_id = params.get("coupon_id");
  const fetchPWLClinics = useStaffClinicListAPI("PATIENT_WAITING_LIST");
  const fetchOrgCities = useConfigurationCityListAPI();
  const { execute: fetcUserPerSona, userpersona } = useCouponUserPerSonaListAPI();
  const { execute: updateCoupons } = useCouponUpdationAPI();
  const { execute: fetchCouponDetails, couponDetails } = useCouponDetailsAPI();
  const orgClinicItems = useSelectMenuReducer(clinics, "display_name", "id");
  const is_membership_coupon = couponDetails?.is_membership_coupon;
  const isQCCoupon = couponDetails?.coupon_type === "QC COUPON";
  // Only non-membership Service and BOM coupons can be base coupons (used to mint user-level copies).
  const canBeBaseCoupon =
    (couponDetails?.coupon_type === "SERVICE" || couponDetails?.coupon_type === "BOM") && !is_membership_coupon;
  // Appointment dependency is a clinic-visit concept — only for item-level BOM/SERVICE coupons.
  const showAppointmentDependency =
    couponDetails?.coupon_type === "SERVICE" || couponDetails?.coupon_type === "BOM";
  const { execute: fetchOrgServices, serviceTypes } = useOrgServicesByFilterAPI();
  // Flat, de-duped list of bookable sub-services sourced from the org service hierarchy.
  const bookableSubServices = useMemo(() => {
    const seen = new Set<string>();
    const list: { sub_service_id: string; sub_service_name: string }[] = [];
    for (const type of serviceTypes ?? []) {
      for (const service of type.services ?? []) {
        for (const sub of service.sub_services ?? []) {
          if (sub?.sub_service_id && !seen.has(sub.sub_service_id)) {
            seen.add(sub.sub_service_id);
            list.push({ sub_service_id: sub.sub_service_id, sub_service_name: sub.sub_service_name });
          }
        }
      }
    }
    return list;
  }, [serviceTypes]);
  const subServiceItems = useSelectMenuReducer(bookableSubServices, "sub_service_name", "sub_service_id");
  const orgCityItemsFromPincode = useSelectMenuReducer(pincodeListing, "city", "city");
  const orgCityItemsFromCityList = useSelectMenuReducer(cityList, "name", "id");
  const orgCityItems = isQCCoupon ? orgCityItemsFromPincode : orgCityItemsFromCityList;
  const { execute: fetchPincode } = usePincodeListingAPI();

  const formHook = useForm<ICouponServiceApplicabilityConditionFromFiled>({
    mode: "onChange",
    defaultValues: {
      allpersona: "active",
      included_operation: "AND",
      excluded_operation: "AND",
      included_segments: "active",
      pincodeEligibility: true,
    },
  });

  const selectedCityNames = useMemo(() => {
    const selectedCityIds = formHook.watch("applicable_city") ?? [];
    return orgCityItems.filter((c) => selectedCityIds.includes(c.id)).map((c) => c.title);
  }, [formHook.watch("applicable_city"), orgCityItems]);

  const formattedPincodeListing = useMemo(
    () =>
      (pincodeListing ?? [])
        .filter((item) => selectedCityNames.includes(item.city))
        .flatMap((item) => item.pincodes)
        .map((pincode) => ({ pincode: String(pincode) })),
    [pincodeListing, selectedCityNames],
  );

  const orgPincodeItems = useSelectMenuReducer(formattedPincodeListing, "pincode", "pincode");

  useEffect(() => {
    if (is_membership_coupon) {
      setIsDisable(is_membership_coupon);
    }
  }, [is_membership_coupon]);

  useEffect(() => {
    if (isQCCoupon) {
      formHook.unregister(["applicable_clinics"]);
    }
  }, [isQCCoupon]);

  const onSubmit = (data: ICouponServiceApplicabilityConditionFromFiled) => {
    if (showAppointmentDependency && data.appointment_dependency && !data.appointment_sub_services?.length) {
      toast.error("Select at least one sub-service for appointment dependency");
      return;
    }
    const constructedBody: ICouponServiceProps = {
      coupon_id: coupon_id,
      page_step: "applicable_conditions",
      coupon_type: couponDetails?.coupon_type,
      base_coupon: data.base_coupon ?? false,
      ...(showAppointmentDependency && {
        appointment_dependency: data.appointment_dependency ?? false,
        appointment_sub_services: data.appointment_dependency ? data.appointment_sub_services : [],
      }),
    };
    if (!is_membership_coupon) {
      const applicableConditions: any = {};
      if (isQCCoupon) {
        applicableConditions.pincode_eligibility = data.pincodeEligibility ? true : false;
        applicableConditions.pincodes = data.pincodes ?? [];
        applicableConditions.applicable_city = allCitiesSelected ? ["all_inclusive"] : data.applicable_city;
      } else {
        applicableConditions.applicable_city = allCitiesSelected ? ["all_inclusive"] : data.applicable_city;
        applicableConditions.applicable_clinics = allClinicsSelected ? ["all_inclusive"] : data.applicable_clinics;
      }
      if (data.minimum_cart_value_required !== "NO") {
        // applicableConditions.minimum_cart_value = parseInt(data.minimum_cart_value);
      }
      if (data.allpersona !== "active") {
        applicableConditions.persona_included = {
          included_segments:
            data.include_persona.length === userpersona?.length ? ["all_inclusive"] : data.include_persona,
          included_operation: data.included_operation,
        };
      } else {
        applicableConditions.persona_included = {
          included_segments: ["all_inclusive"],
          included_operation: null,
        };
      }
      if (data.exclude_persona?.length > 0 && data.allexclude) {
        applicableConditions.persona_excluded = {
          excluded_segments:
            data.exclude_persona.length === userpersona?.length ? ["all_inclusive"] : data.exclude_persona,
          excluded_operation: data.excluded_operation,
        };
      }
      if (data.base_coupon) {
        // Base coupons are never persona-scoped (backend rejects otherwise).
        applicableConditions.persona_included = { included_segments: ["all_inclusive"], included_operation: null };
        delete applicableConditions.persona_excluded;
      }
      constructedBody.applicable_conditions = applicableConditions;
    }
    setIsSubmitting(true);
    updateCoupons(constructedBody).then(({ status }) => {
      setIsSubmitting(false);
      if (status) {
        navigation(`/app/coupon-service/coupon-creation?page_step=discount_page&&coupon_id=${coupon_id}`);
      }
    });
  };

  useEffect(() => {
    const applicability_conditions = couponDetails?.applicable_conditions;
    if (applicability_conditions && orgCityItems?.length > 0) {
      if (applicability_conditions.pincodeEligibility !== undefined) {
        formHook.setValue("pincodeEligibility", applicability_conditions.pincodeEligibility);
      }
      setAllCitiesSelected(applicability_conditions.applicable_city?.[0] === "all_inclusive");
      formHook.setValue(
        "applicable_city",
        applicability_conditions.applicable_city[0] === "all_inclusive"
          ? orgCityItems.map((i) => i.id)
          : applicability_conditions?.applicable_city,
      );
      formHook.setValue(
        "allpersona",
        applicability_conditions.persona_included.included_segments[0] === "all_inclusive" ? "active" : "inactive",
      );
      formHook.setValue("include_persona", applicability_conditions.persona_included.included_segments);
      formHook.setValue("included_operation", applicability_conditions.persona_included.included_operation);
      formHook.setValue(
        "allexclude",
        applicability_conditions?.persona_excluded?.excluded_segments?.length > 0 ? true : false,
      );
      formHook.setValue(
        "exclude_persona",
        applicability_conditions?.persona_excluded?.excluded_segments[0] === "all_inclusive"
          ? userpersona.map((i) => i)
          : applicability_conditions?.persona_excluded?.excluded_segments,
      );
      formHook.setValue("excluded_operation", applicability_conditions?.persona_excluded?.excluded_operation);
    }
  }, [couponDetails, orgCityItems?.length]);

  useEffect(() => {
    const applicability_conditions = couponDetails?.applicable_conditions;
    if (isQCCoupon && orgPincodeItems?.length > 0 && !initialPincodeFetch) {
      formHook.setValue("pincodes", applicability_conditions?.pincodes ?? []);
      setInitialPincodeFetch(true);
    }
  }, [couponDetails, orgPincodeItems?.length, initialPincodeFetch, isQCCoupon]);

  useEffect(() => {
    const applicability_conditions = couponDetails?.applicable_conditions;
    if (orgClinicItems?.length > 0 && !initialfetch) {
      setAllClinicsSelected(applicability_conditions?.applicable_clinics?.[0] === "all_inclusive");
      formHook.setValue(
        "applicable_clinics",
        applicability_conditions?.applicable_clinics[0] === "all_inclusive"
          ? orgClinicItems?.map((i) => i.id)
          : applicability_conditions?.applicable_clinics,
      );
      setInitialfetch(true);
    }
  }, [couponDetails, orgClinicItems?.length > 0, initialfetch]);

  useEffect(() => {
    if (coupon_id) {
      setInitialfetch(false);
      setInitialPincodeFetch(false);
      fetchCouponDetails(coupon_id);
    }
  }, [coupon_id]);

  useEffect(() => {
    fetchOrgServices({ filterType: "booking_vms" });
  }, []);

  useEffect(() => {
    if (couponDetails) {
      formHook.setValue("base_coupon", couponDetails?.base_coupon ?? false);
      formHook.setValue("appointment_dependency", couponDetails?.appointment_dependency ?? false);
      formHook.setValue("appointment_sub_services", couponDetails?.appointment_sub_services ?? []);
    }
  }, [couponDetails]);

  useEffect(() => {
    const userPerSonaItem = userpersona?.map((item, index) => ({
      id: index + 1,
      title: item,
    }));
    setPerSonaItem(userPerSonaItem);
  }, [userpersona]);

  useEffect(() => {
    const persona = formHook.watch("include_persona");
    if (Array.isArray(persona) && Array.isArray(userpersona)) {
      const filteredPersona = userpersona.filter((i) => !persona.includes(i));
      const userPerSonaItem = filteredPersona.map((item, index) => ({
        id: index + 1,
        title: item,
      }));
      setExcludeperSonaItem(userPerSonaItem);
    } else if (typeof persona === "string" && Array.isArray(userpersona)) {
      const filteredPersona = userpersona.filter((i) => i !== persona);
      const userPerSonaItem = filteredPersona.map((item, index) => ({
        id: index + 1,
        title: item,
      }));
      setExcludeperSonaItem(userPerSonaItem);
    } else {
      setExcludeperSonaItem([]);
    }
  }, [formHook.watch("include_persona"), userpersona]);

  useEffect(() => {
    fetcUserPerSona();
    fetchOrgCities();
    fetchPincode();
  }, []);

  useEffect(() => {
    if (isQCCoupon) return;
    const formFields = formHook.getValues();
    if (formFields.applicable_city?.length > 0) {
      fetchPWLClinics({
        cityIds: formFields.applicable_city,
        withProviders: false,
      });
    }
  }, [formHook.watch("applicable_city"), isQCCoupon]);

  useEffect(() => {
    if (isQCCoupon) return;
    const formFields = formHook.getValues();
    if (formFields.applicable_city?.length > 0) {
      const city =
        formFields.applicable_city[0] === "all_inclusive"
          ? orgCityItems.map((i) => i.id)
          : couponDetails?.applicable_conditions?.applicable_city;
      fetchPWLClinics({
        cityIds: city?.join() as any,
        withProviders: false,
      });
    }
  }, [formHook.watch("applicable_city"), isQCCoupon]);

  const handelBackStepper = () => {
    navigation(`/app/coupon-service/coupon-creation?page_step=details_and_schedule&&coupon_id=${coupon_id}`);
  };

  const defaultSelected = useMemo(() => {
    const excludePersona = formHook.watch("exclude_persona");
    return excludePersona?.length > 0 ? formHook.getValues("exclude_persona") : [];
  }, [formHook.watch("exclude_persona")]);

  const handleSelectItem = useCallback(
    (items) => {
      formHook.setValue("exclude_persona", items);
    },
    [formHook],
  );

  const defaultSelectedPersona = useMemo(() => {
    const excludePersona = formHook.watch("include_persona");
    return excludePersona?.length > 0 ? formHook.getValues("include_persona") : [];
  }, [formHook.watch("include_persona")]);

  const handleSelectItemPersona = useCallback(
    (items) => {
      formHook.setValue("include_persona", items);
    },
    [formHook],
  );

  useEffect(() => {
    if (formHook.watch("include_persona")?.length === perSonaItem?.length) {
      formHook.setValue("allexclude", false);
    }
  }, [formHook.watch("include_persona"), perSonaItem]);

  return (
    <div className="p-4 bg-white">
      <div className="p-3 py-3">
        <h2 className="text-sm font-bold text-gray-800 tracking-wide uppercase mb-4">Applicability Conditions</h2>
        <form className="w-full mt-2" onSubmit={formHook.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-5 w-full mb-16">
            {isQCCoupon ? (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0">
                  <div className="w-full sm:w-2/12">
                    <p className="text-sm font-semibold text-gray-700">
                      Pincode Eligibility <span className="text-red-400">*</span>
                    </p>
                  </div>
                  <div className="w-full sm:w-5/12 lg:w-4/12">
                    <Toggle
                      checked={formHook?.watch("pincodeEligibility") || false}
                      onToggle={(state) => {
                        // console.log(state);
                        formHook.setValue("pincodeEligibility", state);
                        if (!state) {
                          formHook.clearErrors("pincodes");
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0">
                  <div className="w-full sm:w-2/12">
                    <p className="text-sm font-semibold text-gray-700">
                      Cities <span className="text-red-400">*</span>
                    </p>
                  </div>
                  <div className="w-full sm:w-5/12 lg:w-4/12">
                    <SearchableMultiSelectMenu
                      onSelectItem={(items, meta) => {
                        const cityId = items?.map((i) => i?.id);
                        setAllCitiesSelected(!!meta?.selectAllUsed);
                        formHook.setValue("applicable_city", cityId);
                      }}
                      errorMessages={[{ message: "", type: "required" }]}
                      fieldError={formHook.formState.errors.applicable_city}
                      register={formHook.register("applicable_city", {
                        required: !is_membership_coupon && !isQCCoupon,
                      })}
                      withSelectAll
                      showTooltips={true}
                      showTypedErrors={false}
                      placeholder="Select City"
                      defaultSelected={orgCityItems.filter((c) => formHook.watch("applicable_city")?.includes(c.id))}
                      selectItems={orgCityItems}
                      disabled={is_membership_coupon || !formHook.watch("pincodeEligibility")}
                      containerClassName="w-full"
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0">
                  <div className="w-full sm:w-2/12">
                    <p className="text-sm font-semibold text-gray-700">
                      Pincode <span className="text-red-400">*</span>
                    </p>
                  </div>
                  <div className="w-full sm:w-5/12 lg:w-4/12">
                    <SearchableMultiSelectMenu
                      key={initialPincodeFetch ? "pincode-loaded" : "pincode-loading"}
                      onSelectItem={(items) => {
                        const pincodeIds = items?.map((i) => i.id) ?? [];
                        formHook.setValue("pincodes", pincodeIds);
                      }}
                      errorMessages={[{ message: "Pincode is required", type: "required" }]}
                      fieldError={formHook.formState.errors.pincodes}
                      register={formHook.register("pincodes", {
                        required: isQCCoupon && formHook.watch("pincodeEligibility"),
                      })}
                      selectItems={orgPincodeItems}
                      placeholder="Select Pincode"
                      withSelectAll
                      showTooltips={false}
                      showTypedErrors={false}
                      showDropdownIcon={true}
                      defaultSelected={orgPincodeItems.filter((p) => formHook.watch("pincodes")?.includes(p.id))}
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-full"
                      withDropDownSearch
                      disabled={!formHook.watch("pincodeEligibility")}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0">
                  <div className="w-full sm:w-2/12">
                    <p className="text-sm font-semibold text-gray-700">
                      Cities <span className="text-red-400">*</span>
                    </p>
                  </div>
                  <div className="w-full sm:w-5/12 lg:w-4/12">
                    <SearchableMultiSelectMenu
                      onSelectItem={(items, meta) => {
                        const cityId = items?.map((i) => i?.id);
                        setAllCitiesSelected(!!meta?.selectAllUsed);
                        setAllClinicsSelected(false);
                        formHook.setValue("applicable_city", cityId);
                        formHook.setValue("applicable_clinics", []);
                        fetchPWLClinics({
                          cityIds: cityId,
                          withProviders: true,
                        });
                      }}
                      errorMessages={[{ message: "", type: "required" }]}
                      fieldError={formHook.formState.errors.applicable_city}
                      register={formHook.register("applicable_city", {
                        required: !is_membership_coupon && !isQCCoupon,
                      })}
                      withSelectAll
                      showTooltips={true}
                      showTypedErrors={false}
                      placeholder="Select City"
                      defaultSelected={orgCityItems.filter((c) => formHook.watch("applicable_city")?.includes(c.id))}
                      selectItems={orgCityItems}
                      disabled={is_membership_coupon || !formHook.watch("pincodeEligibility")}
                      containerClassName="w-full"
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0">
                  <div className="w-full sm:w-2/12">
                    <p className="text-sm font-semibold text-gray-700">
                      Clinics <span className="text-red-400">*</span>
                    </p>
                  </div>
                  <div className="w-full sm:w-5/12 lg:w-4/12">
                    <SearchableMultiSelectMenu
                      key={(formHook.watch("applicable_city") ?? []).join()}
                      onSelectItem={(items, meta) => {
                        const clinicId = items.map((i) => i.id);
                        setAllClinicsSelected(!!meta?.selectAllUsed);
                        formHook.setValue("applicable_clinics", clinicId);
                      }}
                      errorMessages={[{ message: "", type: "required" }]}
                      fieldError={formHook.formState.errors.applicable_clinics}
                      register={formHook.register("applicable_clinics", {
                        required: !is_membership_coupon && !isQCCoupon,
                      })}
                      withSelectAll
                      showTooltips={true}
                      showTypedErrors={false}
                      placeholder="Select Clinic"
                      defaultSelected={orgClinicItems.filter((c) =>
                        formHook.watch("applicable_clinics")?.includes(c.id),
                      )}
                      selectItems={orgClinicItems}
                      disabled={is_membership_coupon || !formHook.watch("pincodeEligibility")}
                      containerClassName="w-full"
                    />
                  </div>
                </div>
              </>
            )}

            {!formHook.watch("base_coupon") && (
            <div className="mt-6 border-t border-gray-100 pt-6">
              <h3 className="text-sm font-bold text-gray-800 tracking-wide uppercase mb-1">User Persona</h3>
              <p className="text-[12px] text-red-500 font-medium mb-4">
                * If you create app specification coupon select only OR condition in persona
              </p>

              <div className="flex flex-col sm:flex-row sm:items-start py-2 gap-1.5 sm:gap-0">
                <div className="w-full sm:w-2/12 sm:pt-1">
                  <p className="text-sm font-semibold text-gray-700">Target Audience</p>
                </div>
                <div className="w-full sm:w-5/12 lg:w-4/12">
                  <div>
                    <RadioGroupComponent
                      alignment="horizontal"
                      errorMessages={[]}
                      fieldError={formHook.formState.errors.included_segments}
                      register={formHook.register("allpersona")}
                      groupOptions={[
                        { id: "active", title: "All" },
                        { id: "inactive", title: "Select Persona" },
                      ]}
                      defaultSelectedId={formHook.watch("allpersona")}
                      onRadioChange={(optionId: string) => {
                        formHook.setValue("include_persona", []);
                        formHook.setValue("included_operation", "AND");
                        formHook.setValue("allpersona", optionId as any);
                      }}
                      radioOptionClassName="text-sm text-gray-700 font-medium"
                      disabled={isDisable}
                    />
                  </div>
                  {formHook.watch("allpersona") === "inactive" && (
                    <div className="mt-3 flex flex-col gap-4 border border-gray-100 rounded-lg p-4 bg-gray-50/50">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <span className="text-xs font-semibold text-gray-700">Include Using:</span>
                        <RadioGroupComponent
                          alignment="horizontal"
                          errorMessages={[]}
                          fieldError={formHook.formState.errors.included_operation}
                          register={formHook.register("included_operation")}
                          groupOptions={[
                            { id: "AND", title: "AND" },
                            { id: "OR", title: "OR" },
                          ]}
                          defaultSelectedId={formHook.watch("included_operation")}
                          onRadioChange={(optionId: string) => {
                            formHook.setValue("included_operation", optionId as any);
                          }}
                          radioOptionClassName="text-sm"
                          disabled={isDisable}
                        />
                      </div>
                      <div className="w-full">
                        <MultiSelectDropdown
                          data={perSonaItem}
                          selectedKey="title"
                          onSelectItem={handleSelectItemPersona}
                          defaultSelected={defaultSelectedPersona}
                          required={true}
                          register={formHook.register}
                          name="include_persona"
                          error={formHook.formState.errors.include_persona as any}
                          containerClassName="w-full"
                        />
                      </div>
                    </div>
                  )}
                  <div className="mt-4">
                    <SimpleCheckbox
                      onChange={() => {
                        if (formHook.watch("allexclude")) {
                          formHook.setValue("allexclude", false);
                        } else {
                          formHook.setValue("allexclude", true);
                        }
                      }}
                      checked={formHook.watch("allexclude")}
                      id="expiry"
                      colorClassName="text-[#202223] cursor-pointer"
                      descriptionContent={
                        <label htmlFor="expiry" className="ml-3 block text-xs font-medium leading-6 text-gray-700">
                          Exclude Users
                        </label>
                      }
                      disabled={isDisable || formHook.watch("include_persona")?.length === perSonaItem?.length}
                    />
                    {formHook.watch("allexclude") && (
                      <div className="mt-3 flex flex-col gap-4 border border-gray-100 rounded-lg p-4 bg-gray-50/50">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                          <span className="text-xs font-semibold text-gray-700">Excluded Using:</span>
                          <RadioGroupComponent
                            alignment="horizontal"
                            errorMessages={[]}
                            fieldError={formHook.formState.errors.excluded_operation}
                            register={formHook.register("excluded_operation")}
                            groupOptions={[
                              { id: "AND", title: "AND" },
                              { id: "OR", title: "OR" },
                            ]}
                            defaultSelectedId={formHook.watch("excluded_operation")}
                            onRadioChange={(optionId: string) => {
                              formHook.setValue("excluded_operation", optionId as any);
                            }}
                            radioOptionClassName="text-sm"
                          />
                        </div>
                        <div className="w-full">
                          <MultiSelectDropdown
                            data={excludeperSonaItem}
                            selectedKey="title"
                            onSelectItem={handleSelectItem}
                            defaultSelected={defaultSelected}
                            required={true}
                            register={formHook.register}
                            name="exclude_persona"
                            error={formHook.formState.errors.exclude_persona as any}
                            containerClassName="w-full"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            )}

            {canBeBaseCoupon && (
            <div className="mt-6 border-t border-gray-100 pt-6">
              <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0">
                <div className="w-full sm:w-2/12">
                  <p className="text-sm font-semibold text-gray-700">Base Coupon</p>
                </div>
                <div className="w-full sm:w-5/12 lg:w-4/12">
                  <Toggle
                    checked={formHook?.watch("base_coupon") || false}
                    onToggle={(state) => {
                      formHook.setValue("base_coupon", state);
                      if (state) {
                        // Base coupons cannot be persona-scoped.
                        formHook.setValue("allpersona", "active");
                        formHook.setValue("include_persona", []);
                        formHook.setValue("allexclude", false);
                      }
                    }}
                  />
                </div>
              </div>
              <p className="text-[12px] text-gray-500 font-medium mt-1">
                Base coupons are hidden everywhere and used only to create user-level copies. Enabling this
                disables persona targeting and turns off all visibility / auto-apply.
              </p>
            </div>
            )}
            {showAppointmentDependency && (
              <div className="mt-6 border-t border-gray-100 pt-6">
                <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0">
                  <div className="w-full sm:w-2/12">
                    <p className="text-sm font-semibold text-gray-700">Appointment dependency</p>
                  </div>
                  <div className="w-full sm:w-5/12 lg:w-4/12">
                    <Toggle
                      checked={formHook?.watch("appointment_dependency") || false}
                      onToggle={(state) => {
                        formHook.setValue("appointment_dependency", state);
                        if (!state) {
                          formHook.setValue("appointment_sub_services", []);
                          formHook.clearErrors("appointment_sub_services");
                        }
                      }}
                    />
                  </div>
                </div>
                <p className="text-[12px] text-gray-500 font-medium mt-1">
                  When on, the coupon is applicable only while the customer has a live booked visit for one of
                  the selected sub-services (checked in, up to 3 hours after checkout).
                </p>
                {formHook.watch("appointment_dependency") && (
                  <div className="flex flex-col sm:flex-row sm:items-center py-2 gap-1.5 sm:gap-0 mt-3">
                    <div className="w-full sm:w-2/12">
                      <p className="text-sm font-semibold text-gray-700">
                        Applicable sub-services <span className="text-red-400">*</span>
                      </p>
                    </div>
                    <div className="w-full sm:w-5/12 lg:w-4/12">
                      <SearchableMultiSelectMenu
                        onSelectItem={(items) => {
                          formHook.setValue(
                            "appointment_sub_services",
                            (items ?? []).map((i) => i.id),
                          );
                        }}
                        selectItems={subServiceItems}
                        withSelectAll
                        withDropDownSearch
                        placeholder="Select Sub Services"
                        errorMessages={[{ message: "", type: "validate" }]}
                        // Field stays registered after the toggle hides it, so only require it while on.
                        register={formHook.register("appointment_sub_services", {
                          validate: (value) =>
                            !formHook.getValues("appointment_dependency") || (value?.length ?? 0) > 0,
                        })}
                        fieldError={formHook.formState.errors.appointment_sub_services}
                        defaultSelected={subServiceItems.filter((c) =>
                          formHook.watch("appointment_sub_services")?.includes(c.id),
                        )}
                        containerClassName="w-full"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="right-5 bottom-0 fixed flex justify-end items-center gap-4 border-t-2 w-full p-3 bg-white">
            <button
              type="button"
              onClick={() => handelBackStepper()}
              className="text-xs mt-3 inline-flex w-full justify-center rounded-md bg-[#b1b5bb] px-4 py-3 font-semibold text-white ring-inset ring-gray-300 hover-bg-gray-50 sm:mt-0 sm:w-auto"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="text-xs mt-3 inline-flex w-full justify-center items-center gap-2 rounded-md bg-[#193A6A] px-4 py-3 font-semibold text-white ring-inset ring-gray-300 hover-bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting && (
                <svg
                  className="animate-spin mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {isSubmitting ? "Saving..." : "Save & Next"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default CouponApplicabilityComponent;
